import { Link } from "@tanstack/react-router";
import { ArrowRight, Clock, Tag, User } from "lucide-react";
import { InlineText } from "@/components/inline-text";
import { resolveArticlePath } from "@/lib/article-routes";
import type { Article } from "@/lib/content-blocks";

/**
 * The long-form article renderer shared by /tutorials and /geo: eyebrow, meta
 * strip, intro, on-page TOC, body blocks, "Recommended next".
 *
 * "Recommended next" holds absolute article paths (they routinely point across
 * areas). Each card's label is the target's own `title`, so it cannot drift; a
 * path that no longer resolves is dropped rather than linked into a 404.
 */
export function ArticlePage({ item }: { item: Article }) {
  const toc = item.blocks
    .map((b, i) => (b.type === "h2" ? { id: slugify(b.text) + "-" + i, text: b.text } : null))
    .filter((x): x is { id: string; text: string } => x !== null);

  const next = (item.next ?? []).map(resolveArticlePath).filter((n) => n !== null);

  return (
    <article className="min-w-0">
      <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground mb-3">
        {item.category}
      </p>
      <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-5 text-pretty">
        {item.title}
      </h1>

      {/* Meta strip */}
      <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground border-y border-border/60 py-4 mb-8">
        <span className="inline-flex items-center gap-1.5">
          <Tag className="h-3.5 w-3.5" /> {item.product}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5" /> {item.estimatedTime}
        </span>
        <span>{item.difficulty}</span>
        <span className="inline-flex items-center gap-1.5">
          <User className="h-3.5 w-3.5" /> {item.bestFor}
        </span>
      </div>

      {/* Intro */}
      <p className="text-lg leading-relaxed text-foreground/90 mb-10 text-pretty">{item.intro}</p>

      {/* On-page TOC */}
      {toc.length > 0 && (
        <div className="rounded-lg border border-border/60 bg-muted/20 p-5 mb-10">
          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground mb-3">
            On this page
          </p>
          <ul className="space-y-1.5 text-sm">
            {toc.map((entry) => (
              <li key={entry.id}>
                <a
                  href={`#${entry.id}`}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  {entry.text}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Body */}
      <div className="prose-axy max-w-none">
        {item.blocks.map((b, i) => {
          if (b.type === "h2") {
            const id = slugify(b.text) + "-" + i;
            return (
              <h2
                key={i}
                id={id}
                className="scroll-mt-24 text-2xl md:text-[1.6rem] font-semibold tracking-tight mt-12 mb-4 text-foreground"
              >
                {b.text}
              </h2>
            );
          }
          if (b.type === "h3") {
            return (
              <h3
                key={i}
                className="text-lg font-semibold tracking-tight mt-7 mb-2 text-foreground"
              >
                {b.text}
              </h3>
            );
          }
          if (b.type === "ul") {
            return (
              <ul
                key={i}
                className="list-disc pl-6 space-y-1.5 text-[15px] leading-relaxed text-foreground/85 mb-5 marker:text-muted-foreground"
              >
                {b.items.map((it, j) => (
                  <li key={j}>{it}</li>
                ))}
              </ul>
            );
          }
          if (b.type === "callout") {
            return (
              <div
                key={i}
                className="rounded-lg border border-primary/30 bg-primary/5 p-4 my-6 text-sm text-foreground/90"
              >
                {b.text}
              </div>
            );
          }
          if (b.type === "image") {
            if (!b.src) return null;
            return (
              <figure key={i} className="my-6">
                <img
                  src={b.src}
                  alt={b.alt ?? ""}
                  className="w-full rounded-lg border border-border"
                  loading="lazy"
                />
                {b.caption && (
                  <figcaption className="mt-2 text-center text-xs text-muted-foreground">
                    {b.caption}
                  </figcaption>
                )}
              </figure>
            );
          }
          return (
            <p key={i} className="text-[15px] leading-relaxed text-foreground/85 mb-5">
              <InlineText text={b.text} />
            </p>
          );
        })}
      </div>

      {next.length > 0 && (
        <div className="mt-16 pt-10 border-t border-border/60">
          <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground mb-4">
            Recommended next
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {next.map((n) => (
              <Link
                key={n.article.slug}
                to={n.to}
                params={{ slug: n.slug }}
                className="group flex items-center justify-between rounded-xl border border-border/60 bg-card/40 hover:bg-card/70 hover:border-border transition-all p-4"
              >
                <span className="text-sm font-medium group-hover:text-primary transition-colors">
                  {n.article.title}
                </span>
                <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}

function slugify(s: string) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}
